// eslint-disable-next-line
import { Box, IconButton, Tooltip } from '@mui/material';
import clsx from 'clsx';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { MdClose } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';

import {
    getActiveAssetActiveZone,
    getChildZones,
    getSelectedSearchItemId,
    selectActiveAsset,
    selectCompactZones,
} from 'GeminiViewerComponent/features/asset/assetSlice';
import { getConfig } from 'GeminiViewerComponent/features/config/configSlice';
import { selectActiveTheme } from 'GeminiViewerComponent/features/globals/themeSlice';
import {
    getLoadedAssetType,
    getProcedureContentVersion,
    getProcedureLoaded,
    getTagFilters,
} from 'GeminiViewerComponent/features/globals/visibilitySlice';
import { AssetTypes, HotspotIconTypes, ProcedureSplitViewTypes } from 'GeminiViewerComponent/helpers';
import { useAvailableZones } from 'GeminiViewerComponent/hooks/useAvailableZones';
import { loadZone } from 'shared/loadZoneSlice';
import { FlyerDisplay } from '../components/FlyerDisplay';
import { SidePanel } from '../components/SidePanel';
import { GeminiTitleBar } from './Common/GeminiTitleBar';
import { makeCommonStyles } from './Common/styles';
import { MarzipanoViewer } from './MarzipanoViewer';
import { OrbitViewer } from './OrbitViewer';
import { getHotspotRetryCount } from './Procedure/features/procedureSlice';
import { TagBar } from './TagBar';

const SplitViewButton = ({ viewType, splitViewMode, setSplitViewMode }) => {
    return (
        <Tooltip title={viewType?.label ?? ''}>
            <IconButton
                id={`viewer_asset-viewer_${viewType?.selectorPartial}-btn`}
                onClick={() => setSplitViewMode(viewType?.id)}
                style={{
                    padding: 0,
                    backgroundColor: '#ffffff',
                    borderRadius: 'inherit',
                }}
            >
                <Box
                    style={{
                        width: '35px',
                        height: '35px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        borderRadius: '3px',
                        border: '1px solid #ffffff',
                        backgroundColor: `${viewType.id === splitViewMode ? 'white' : 'lightgray'}`,
                    }}
                >
                    <img src={viewType.icon} alt={`split view ${viewType?.type}`} height={'30px'} width={'30px'} />
                </Box>
            </IconButton>
        </Tooltip>
    );
};

const GeminiContainer = ({
    draggableFlag,
    setDraggableFlag,
    hotspotIcons,
    hotspotElements,
    setHotspotElements,
    onSelectHotspot,
    onShiftRightClick,
    onRightClick,
    onClick,
    onUpdatedHotspot,
    onDeletedHotspot,
    onUpdatedLinkHotspot,
    onDeletedLinkHotspot,
    onEditClicked,
    onItemNavClicked,
    onNavClicked,
    onClose,
    PanoItemHotspot,
    PanoLinkHotspot,
    isMultipleHotspotEnabled,
    itemHotspots,
    navigationHotspots,
    searchItemSelected,
    onClickRefreshItem,
    setActiveItem,
    readOnly,
    handleItemDragging,
    handleNavDragging,
    enableItemDragging,
    enableNavDragging,
    undoRedoProps,
    handleMultipleHotspot,
    viewerRef = null,
    splitViewMode = null,
    setSplitViewMode = () => {},
    zoneMediaConfigValue = false,
    emptyItemOpacity,
    setShowAssociatedAsset,
}) => {
    const dispatch = useDispatch();
    const theme = useSelector(selectActiveTheme);
    const commonStyles = makeCommonStyles(theme);
    const INIT_HIDE_NAV_OPTIONS = {
        hideNavBar: false,
        hideNavZones: false,
        hideNavSearch: false,
        hideNavTitle: false,
        hideNavControls: false,
    };
    const [hideNavOptions, setHideNavOptions] = useState(INIT_HIDE_NAV_OPTIONS);
    const hotspotRetryCount = useSelector(getHotspotRetryCount);

    const tridiRef = useRef(null);
    const allZones = useSelector(selectCompactZones);
    const childZones = useSelector(getChildZones);
    const allZoneData = useMemo(() => {
        if (childZones && childZones?.length > 0) {
            return [...allZones, ...childZones];
        } else {
            return allZones;
        }
    }, [allZones, childZones]);
    const config = useSelector(getConfig);
    const isProcedureLoaded = useSelector(getProcedureLoaded);
    const activeAsset = useSelector(selectActiveAsset);
    const selectedSearchItemId = useSelector(getSelectedSearchItemId);
    const loadedAssetType = useSelector(getLoadedAssetType);
    const [itemTagsList, setItemTagsList] = useState([]);
    const tagContainerRef = useRef(null);
    const procedureContentVersion = useSelector(getProcedureContentVersion);
    const hotspotCacheRef = useRef(new Map());

    useEffect(() => {
        if (config.modeType === 'viewer') {
            setHideNavOptions({
                hideNavBar:
                    activeAsset?.options?.hide_nav_zones &&
                    activeAsset?.options?.hide_nav_controls &&
                    activeAsset?.options?.hide_nav_search &&
                    activeAsset?.options?.hide_nav_title,
                hideNavZones: activeAsset?.options?.hide_nav_zones,
                hideNavSearch: activeAsset?.options?.hide_nav_search,
                hideNavTitle: activeAsset?.options?.hide_nav_title,
                hideNavControls: activeAsset?.options?.hide_nav_controls,
            });
        } else if (config.modeType === 'author') {
            setHideNavOptions(INIT_HIDE_NAV_OPTIONS);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [config, isProcedureLoaded, activeAsset]);

    let activeZone = useSelector(getActiveAssetActiveZone);

    const tagFilters = useSelector(getTagFilters);

    const availableZones = useAvailableZones();

    const getZoneCategoryName = (zone) => {
        return zone?.category ?? zone?.zone_category_name ?? '[NONE]';
    };

    const buildMenuData = useCallback(
        (zones) => {
            const sourceZones = Object.values(config.modeType === 'viewer' ? [...allZones, ...childZones] : zones);

            // Prepare containers
            const currentAssetCategories = {};
            const otherAssets = {};

            // Fill
            sourceZones.forEach((zone) => {
                const categoryName = getZoneCategoryName(zone);
                const hasAssetId = !!zone?.asset_id;
                const isActive = hasAssetId ? zone?.asset_id === activeAsset?.asset_id : true;
                if (isActive) {
                    if (!currentAssetCategories[categoryName]) {
                        currentAssetCategories[categoryName] = {
                            open: false,
                            zones: [],
                            category_color: {
                                backgroundColor: theme.colors.button.primaryBackground,
                                color: theme.colors.button.primaryForeground,
                            },
                        };
                    }
                    currentAssetCategories[categoryName].zones.push(zone);
                } else if (hasAssetId) {
                    const assetName = zone?.asset_name ?? `Asset ${zone?.asset_id ?? ''}`;
                    if (!otherAssets[assetName]) {
                        otherAssets[assetName] = {
                            open: false,
                            asset_color: {
                                backgroundColor: theme.colors.primarySectionBackground,
                                color: theme.colors.primarySectionForeground,
                            },
                            categories: {},
                        };
                    }
                    if (!otherAssets[assetName].categories[categoryName]) {
                        otherAssets[assetName].categories[categoryName] = {
                            open: false,
                            zones: [],
                            category_color: {
                                backgroundColor: theme.colors.button.accentBackground,
                                color: theme.colors.button.accentForeground,
                            },
                        };
                    }
                    otherAssets[assetName].categories[categoryName].zones.push(zone);
                }
            });

            // Sort zones in current categories
            Object.values(currentAssetCategories).forEach((cat) => {
                cat.zones.sort((a, b) => {
                    if (!a.display_order && b.display_order) return -1;
                    if (a.display_order && !b.display_order) return 1;
                    if (a.display_order && b.display_order)
                        return a.display_order
                            ?.toString()
                            ?.localeCompare(b.display_order?.toString(), undefined, { numeric: true });
                    return a.display_name.localeCompare(b.display_name);
                });
            });

            // Optional: sort zones in other assets as well
            Object.values(otherAssets).forEach((asset) => {
                Object.values(asset.categories).forEach((cat) => {
                    cat.zones.sort((a, b) => {
                        if (!a.display_order && b.display_order) return -1;
                        if (a.display_order && !b.display_order) return 1;
                        if (a.display_order && b.display_order)
                            return a.display_order
                                ?.toString()
                                ?.localeCompare(b.display_order?.toString(), undefined, { numeric: true });
                        return a.display_name.localeCompare(b.display_name);
                    });
                });
            });

            return { currentAssetCategories, otherAssets };
        },
        [
            config.modeType,
            allZones,
            childZones,
            activeAsset?.asset_id,
            theme.colors.button.primaryBackground,
            theme.colors.button.primaryForeground,
            theme.colors.button.accentBackground,
            theme.colors.button.accentForeground,
            theme.colors.primarySectionBackground,
            theme.colors.primarySectionForeground,
        ]
    );

    const menuInit = buildMenuData(availableZones);

    const [currentAssetCategories, setCurrentAssetCategories] = useState(menuInit.currentAssetCategories);
    const [otherAssetsByCategory, setOtherAssetsByCategory] = useState(menuInit.otherAssets);

    useEffect(() => {
        const menuUpdated = buildMenuData(availableZones);
        setCurrentAssetCategories(menuUpdated.currentAssetCategories);
        setOtherAssetsByCategory(menuUpdated.otherAssets);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tagFilters, availableZones]);

    function handleOtherAssetClick(assetName) {
        const prevOther = otherAssetsByCategory;
        const prevCurr = currentAssetCategories;
        const open = !prevOther?.[assetName]?.open;
        const menuUpdated = buildMenuData(availableZones);

        // Preserve previous open states for other assets and their categories
        Object.keys(prevOther || {}).forEach((a) => {
            if (menuUpdated.otherAssets[a]) {
                menuUpdated.otherAssets[a].open = prevOther[a].open;
                const prevCats = prevOther[a].categories || {};
                Object.keys(prevCats).forEach((c) => {
                    if (menuUpdated.otherAssets[a].categories[c]) {
                        menuUpdated.otherAssets[a].categories[c].open = prevCats[c].open;
                    }
                });
            }
        });
        // Preserve previous open states for current asset categories
        Object.keys(prevCurr || {}).forEach((c) => {
            if (menuUpdated.currentAssetCategories[c]) {
                menuUpdated.currentAssetCategories[c].open = prevCurr[c].open;
            }
        });

        if (menuUpdated?.otherAssets?.[assetName]) {
            menuUpdated.otherAssets[assetName].open = open;
        }

        setOtherAssetsByCategory(menuUpdated.otherAssets);
        setCurrentAssetCategories(menuUpdated.currentAssetCategories);
    }

    function handleOtherAssetCategoryClick(assetName, categoryName) {
        const prevOther = otherAssetsByCategory;
        const prevCurr = currentAssetCategories;
        const prevOpen = !!prevOther?.[assetName]?.categories?.[categoryName]?.open;
        const nextOpen = !prevOpen;

        const menuUpdated = buildMenuData(availableZones);

        // Preserve previous open states for other assets and their categories
        Object.keys(prevOther || {}).forEach((a) => {
            if (menuUpdated.otherAssets[a]) {
                menuUpdated.otherAssets[a].open = prevOther[a].open;
                const prevCats = prevOther[a].categories || {};
                Object.keys(prevCats).forEach((c) => {
                    if (menuUpdated.otherAssets[a].categories[c]) {
                        menuUpdated.otherAssets[a].categories[c].open = prevCats[c].true;
                    } else {
                        menuUpdated.otherAssets[a].categories[c].open = prevCats[c].false;
                    }
                });
            }
        });
        // Preserve previous open states for current asset categories
        Object.keys(prevCurr || {}).forEach((c) => {
            if (menuUpdated.currentAssetCategories[c]) {
                menuUpdated.currentAssetCategories[c].open = prevCurr[c].open;
            }
        });

        // Ensure the asset is open and toggle the selected category
        if (menuUpdated?.otherAssets?.[assetName]) {
            menuUpdated.otherAssets[assetName].open = true;
            if (menuUpdated.otherAssets[assetName].categories[categoryName]) {
                menuUpdated.otherAssets[assetName].categories[categoryName].open = nextOpen;
            }
        }

        setOtherAssetsByCategory(menuUpdated.otherAssets);
        setCurrentAssetCategories(menuUpdated.currentAssetCategories);
    }

    function handleCurrentCategoryClick(categoryName) {
        const prevOther = otherAssetsByCategory;
        const prevCurr = currentAssetCategories;
        const prevOpen = !!prevCurr?.[categoryName]?.open;
        const nextOpen = !prevOpen;

        const menuUpdated = buildMenuData(availableZones);

        // Preserve previous open states for other assets and their categories
        Object.keys(prevOther || {}).forEach((a) => {
            if (menuUpdated.otherAssets[a]) {
                menuUpdated.otherAssets[a].open = prevOther[a].open;
                const prevCats = prevOther[a].categories || {};
                Object.keys(prevCats).forEach((c) => {
                    if (menuUpdated.otherAssets[a].categories[c]) {
                        menuUpdated.otherAssets[a].categories[c].open = prevCats[c].open;
                    }
                });
            }
        });
        // Preserve previous open states for current asset categories
        Object.keys(prevCurr || {}).forEach((c) => {
            if (menuUpdated.currentAssetCategories[c]) {
                menuUpdated.currentAssetCategories[c].open = prevCurr[c].open;
            }
        });

        if (menuUpdated?.currentAssetCategories?.[categoryName]) {
            menuUpdated.currentAssetCategories[categoryName].open = nextOpen;
        }
        Object.keys(menuUpdated.currentAssetCategories).forEach((c) => {
            if (c !== categoryName) {
                menuUpdated.currentAssetCategories[c].open = false;
            }
        });
        setCurrentAssetCategories(menuUpdated.currentAssetCategories);
        setOtherAssetsByCategory(menuUpdated.otherAssets);
    }

    const isNavigateToItemQuestion =
        procedureContentVersion?.doAction?.navigate_to_item === true &&
        procedureContentVersion?.doAction?.answer_group === 'hotspot';

    const hotspotItemIcon = useCallback(
        (item) => {
            if (procedureContentVersion?.doAction?.item_id === item.item_id) {
                // don't highlighted icon for answer buttons with hotspot_options
                if (procedureContentVersion?.doAction?.hotspot_options?.length > 0 || isNavigateToItemQuestion) {
                    let icon =
                        hotspotIcons?.length > 0 &&
                        hotspotIcons.find((data) => data?.hotspot_type_id === HotspotIconTypes.Item.id);
                    if (icon) {
                        return icon;
                    }
                } else if (!searchItemSelected) {
                    let icon =
                        hotspotIcons?.length > 0 &&
                        hotspotIcons.find(
                            (data) => data?.is_highlight && data?.hotspot_type_id === HotspotIconTypes.Item.id
                        );
                    if (icon) {
                        return icon;
                    }
                }
            }

            // highlighted icon for search selected item
            if (selectedSearchItemId && selectedSearchItemId === item.item_id && !isNavigateToItemQuestion) {
                let icon =
                    hotspotIcons?.length > 0 &&
                    hotspotIcons.find(
                        (data) => data?.is_highlight && data?.hotspot_type_id === HotspotIconTypes.Item.id
                    );
                if (icon) {
                    return icon;
                }
            }

            const iconId =
                item.hotspot_icon_id || item.hotspot_icon_id === 0
                    ? item.hotspot_icon_id
                    : item.hotspot_icon
                    ? item.hotspot_icon.hotspot_icon_id
                    : activeAsset.item_hotspot_icon_id;
            if (!iconId || iconId === 0) {
                const defaultIcon =
                    hotspotIcons?.length > 0 &&
                    hotspotIcons.find((data) => data?.is_default && data?.hotspot_type_id === HotspotIconTypes.Item.id);
                if (!defaultIcon) {
                    return hotspotIcons.find((data) => data?.hotspot_type_id === HotspotIconTypes.Item.id);
                }
                return defaultIcon;
            }
            return hotspotIcons?.length > 0 && hotspotIcons.find((data) => data?.hotspot_icon_id === iconId);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            selectedSearchItemId,
            procedureContentVersion?.doAction?.item_id,
            procedureContentVersion?.doAction?.navigate_to_item,
            procedureContentVersion?.doAction?.answer_group,
            activeAsset.item_hotspot_icon_id,
            hotspotIcons,
            isNavigateToItemQuestion,
        ]
    );

    const hotspotNavIcon = (link) => {
        const iconId =
            link.hotspot_icon_id || link.hotspot_icon_id === 0
                ? link.hotspot_icon_id
                : link.hotspot_icon
                ? link.hotspot_icon.hotspot_icon_id
                : activeAsset.nav_hotspot_icon_id;
        if (!iconId || iconId === 0) {
            const defaultIcon =
                hotspotIcons?.length > 0 &&
                hotspotIcons.find(
                    (data) => data?.is_default && data?.hotspot_type_id === HotspotIconTypes.Navigation.id
                );

            if (!defaultIcon) {
                return hotspotIcons.find((data) => data?.hotspot_type_id === HotspotIconTypes.Navigation.id);
            }
            return defaultIcon;
        }
        return hotspotIcons?.length > 0 && hotspotIcons.find((data) => data?.hotspot_icon_id === iconId);
    };

    function CreateHotspotElementsFromZone(zoneInfo) {
        const zoneId = zoneInfo.zone_id;
        const items = zoneInfo.items ?? [];
        const navLinks = zoneInfo.nav_links ?? [];

        const cache = hotspotCacheRef.current;

        let zoneMap = cache.get(zoneId);
        if (!zoneMap) {
            zoneMap = new Map();
            cache.set(zoneId, zoneMap);
        }

        const nextKeys = new Set();
        const hotspots = [];

        for (const item of items) {
            const key = `item:${item.item_id}`;
            nextKeys.add(key);

            let entry = zoneMap.get(key);
            if (!entry) {
                const element = document.createElement('div');
                element.classList.add('hotspot', 'info-hotspot');
                element.id = `info-${item.item_id}`;
                entry = { element, root: createRoot(element) };
                zoneMap.set(key, entry);
            }

            entry.root.render(
                <PanoItemHotspot
                    zoneId={zoneId}
                    config={config}
                    hotspot={item}
                    icon={hotspotItemIcon(item)}
                    onClicked={onSelectHotspot}
                    setDraggableFlag={setDraggableFlag}
                    readOnly={readOnly}
                    enableDragging={enableItemDragging}
                    emptyItemOpacity={emptyItemOpacity}
                    selectedSearchItemId={selectedSearchItemId}
                    disableHightlight={
                        procedureContentVersion?.doAction?.hotspot_options?.length > 0 || isNavigateToItemQuestion
                    }
                    hideHotspot={
                        procedureContentVersion?.doAction &&
                        'show_hotspot' in procedureContentVersion.doAction &&
                        !procedureContentVersion?.doAction?.show_hotspot
                    }
                    hideHotspotLabel={
                        procedureContentVersion?.doAction &&
                        'show_hotspot_label' in procedureContentVersion.doAction &&
                        !procedureContentVersion?.doAction?.show_hotspot_label
                    }
                />
            );

            hotspots.push({ item_id: item.item_id, element: entry.element });
        }

        for (const navLink of navLinks) {
            const key = `nav:${navLink.nav_link_id}`;
            nextKeys.add(key);

            let entry = zoneMap.get(key);
            if (!entry) {
                const element = document.createElement('div');
                element.classList.add('hotspot', 'link-hotspot');
                element.id = `link-${navLink.nav_link_id}`;
                entry = { element, root: createRoot(element) };
                zoneMap.set(key, entry);
            }

            entry.root.render(
                <PanoLinkHotspot
                    config={config}
                    hotspot={navLink}
                    icon={hotspotNavIcon(navLink)}
                    allZones={allZoneData}
                    onNavClicked={onLocalNavClicked}
                    setDraggableFlag={setDraggableFlag}
                    readOnly={readOnly}
                    enableDragging={enableNavDragging}
                />
            );

            hotspots.push({ nav_link_id: navLink.nav_link_id, element: entry.element });
        }

        for (const [key, entry] of zoneMap) {
            if (!nextKeys.has(key)) {
                entry.root.unmount();
                zoneMap.delete(key);
            }
        }

        return hotspots;
    }

    // Marzipano consumes these elements from a passive effect. Create them in a
    // layout effect so a remount caused by changing to the floating procedure
    // view supplies new elements before Marzipano rebuilds the active scene.
    useLayoutEffect(() => {
        if (setHotspotElements) {
            const hotspotByZone = {};

            const zone = allZoneData.find((zone) => zone.zone_id === activeZone.zone_id);

            if (!zone) return;

            const hotspots = CreateHotspotElementsFromZone(zone);
            hotspotByZone[zone.zone_id] = hotspots;

            setHotspotElements(hotspotByZone);
        }
    }, [
        activeZone,
        allZoneData,
        config,
        activeAsset,
        hotspotIcons,
        selectedSearchItemId,
        hotspotRetryCount,
        procedureContentVersion?.doAction,
    ]);

    useEffect(() => {
        return () => {
            const cache = hotspotCacheRef.current;
            const toDispose = [];
            for (const zoneMap of cache.values()) {
                for (const entry of zoneMap.values()) {
                    toDispose.push(entry);
                }
            }
            cache.clear();

            // A GeminiContainer remounts when the procedure is moved to the
            // popup. A microtask can still run while React is rendering that
            // replacement tree, which makes React warn when a hotspot root is
            // synchronously unmounted. Run this after the current commit.
            setTimeout(() => {
                for (const entry of toDispose) {
                    try {
                        entry.root.unmount();
                    } catch {
                        console.error('Failed to unmount hotspot root', entry);
                    }
                }
            });
        };
    }, []);


    const onLocalNavClicked = async (zoneId) => {
        const zone = allZoneData.find((zone) => zone.zone_id === zoneId);
        if (zone) {
            dispatch(loadZone({ zoneId: zone.zone_id }));
        }
    };

    if (!activeZone || !activeZone.display_name) {
        return null;
    }

    const handleResetPosition = () => {
        if (window.geminiViewManager) {
            window.geminiViewManager.resetViewParams();
        }
    };

    const orbitMode = typeof activeZone?.orbit_frame === 'number' && activeZone.orbit_frame >= 0;

    return (
        <div style={{ height: '100%', position: 'relative', overflow: 'hidden' }}>
            <GeminiTitleBar
                hideNavOptions={hideNavOptions}
                activeZone={activeZone}
                zonesByCategory={currentAssetCategories}
                otherAssetsByCategory={otherAssetsByCategory}
                onAssetClick={handleOtherAssetClick}
                onOtherAssetCategoryClick={handleOtherAssetCategoryClick}
                onCategoryClick={handleCurrentCategoryClick}
                searchItemSelected={searchItemSelected}
                handleResetPosition={handleResetPosition}
                onClose={onClose}
                onClickRefreshItem={onClickRefreshItem}
                handleItemDragging={handleItemDragging}
                handleNavDragging={handleNavDragging}
                enableItemDragging={enableItemDragging}
                enableNavDragging={enableNavDragging}
                undoRedoProps={undoRedoProps}
                isOrbitMode={orbitMode}
                tridiRef={tridiRef}
                zoneMediaConfigValue={zoneMediaConfigValue}
            />
            <div style={{ height: 'calc(100% - 40px)', width: '100%', position: 'relative', zIndex: 0 }}>
                {isProcedureLoaded && config.modeType === 'viewer' && !activeAsset?.options?.hide_viewer_layout_panel && (
                    <Box className={commonStyles.splitViewModes}>
                        {Object.keys(ProcedureSplitViewTypes).map(
                            (viewType) =>
                                viewType !== 'None' && viewType !== 'Window' && (
                                    <SplitViewButton
                                        key={`${ProcedureSplitViewTypes[viewType]?.id}`}
                                        viewType={ProcedureSplitViewTypes[viewType]}
                                        splitViewMode={splitViewMode}
                                        setSplitViewMode={setSplitViewMode}
                                    />
                                )
                        )}
                        {loadedAssetType === AssetTypes.Procedure && (
                            <Tooltip title="Hide Asset">
                                <IconButton
                                    id="viewer_asset-viewer_hide-asset-btn"
                                    onClick={() => {
                                        setShowAssociatedAsset(false);
                                    }}
                                    style={{
                                        padding: 0,
                                        color: '#ffffff',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    <MdClose className="react-icon" fontWeight={'1000'} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                )}
                <div
                    ref={viewerRef}
                    className={clsx([
                        commonStyles.viewWrapper,
                        orbitMode ? commonStyles.orbitViewWrapper : commonStyles.panoViewWrapper,
                    ])}
                    style={{ height: '100%' }}
                    // onPointerMove={(e) => onDragTagContainer(e)}
                    // onPointerUp={(e) => setAllowMoving(false)}
                >
                    {orbitMode ? (
                        <OrbitViewer
                            config={config}
                            hotspotIcons={hotspotIcons}
                            draggableFlag={draggableFlag}
                            setDraggableFlag={setDraggableFlag}
                            onSelectHotspot={onSelectHotspot}
                            onShiftRightClick={onShiftRightClick}
                            onRightClick={onRightClick}
                            onUpdatedHotspot={onUpdatedHotspot}
                            onDeletedHotspot={onDeletedHotspot}
                            onUpdatedLinkHotspot={onUpdatedLinkHotspot}
                            onDeletedLinkHotspot={onDeletedLinkHotspot}
                            onEditClicked={onEditClicked}
                            onItemNavClicked={onItemNavClicked}
                            onNavClicked={onNavClicked ? onNavClicked : onLocalNavClicked}
                            onClose={onClose}
                            PanoItemHotspot={PanoItemHotspot}
                            PanoLinkHotspot={PanoLinkHotspot}
                            onClick={onClick}
                            isMultipleHotspotEnabled={isMultipleHotspotEnabled}
                            itemHotspots={itemHotspots}
                            navigationHotspots={navigationHotspots}
                            zonesByCategory={currentAssetCategories}
                            onCategoryClick={handleCurrentCategoryClick}
                            onClickRefreshItem={onClickRefreshItem}
                            hideNavOptions={hideNavOptions}
                            enableItemDragging={enableItemDragging}
                            enableNavDragging={enableNavDragging}
                            undoRedoProps={undoRedoProps}
                            tridiRef={tridiRef}
                            splitViewMode={splitViewMode}
                            viewerRef={viewerRef}
                        />
                    ) : (
                        <MarzipanoViewer
                            hotspotElements={hotspotElements}
                            onShiftRightClick={onShiftRightClick}
                            onRightClick={onRightClick}
                            onClick={onClick}
                            isMultipleHotspotEnabled={isMultipleHotspotEnabled}
                            onClose={onClose}
                            zonesByCategory={currentAssetCategories}
                            onCategoryClick={handleCurrentCategoryClick}
                            onClickRefreshItem={onClickRefreshItem}
                            setActiveItem={setActiveItem}
                            hideNavOptions={hideNavOptions}
                            undoRedoProps={undoRedoProps}
                            handleMultipleHotspot={handleMultipleHotspot}
                        />
                    )}
                    {config.modeType === 'author' && (
                        <>
                            <TagBar
                                tagContainerRef={tagContainerRef}
                                itemTagsList={itemTagsList}
                                setItemTagsList={setItemTagsList}
                            />
                            <SidePanel
                                linkTypes={config.linkConfig.LinkTypes}
                                audienceIds={config.user.audienceIds}
                                zoneMediaConfigValue={zoneMediaConfigValue}
                            />
                            <FlyerDisplay />
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export { GeminiContainer };
